<template>
  <div class="c-auth">
    <aside class="c-auth__brand">
      <div class="c-auth__brand-glow" />
      <img
        class="c-auth__logo"
        src="../../assets/betty-logo.svg"
        alt="Meridianbet"
      >

      <div class="c-auth__pitch">
        <h1 class="c-auth__pitch-title">
          Backoffice
        </h1>
        <p class="c-auth__pitch-text">
          Manage websites, translations and configuration for every Meridianbet market from one place.
        </p>

        <ul class="c-auth__features">
          <li
            v-for="feature in features"
            :key="feature.label"
            class="c-auth__feature"
          >
            <span class="c-auth__feature-icon">
              <app-icon
                :name="feature.icon"
                :size="18"
              />
            </span>
            {{ feature.label }}
          </li>
        </ul>
      </div>

      <p class="c-auth__copyright">
        © {{ year }} Meridianbet
      </p>
    </aside>

    <main class="c-auth__main">
      <button
        type="button"
        class="ui-btn ui-btn--ghost c-auth__theme-toggle"
        :aria-label="isNightMode ? 'Switch to light theme' : 'Switch to dark theme'"
        :title="isNightMode ? 'Light theme' : 'Dark theme'"
        @click="handleThemeToggle(!isNightMode)"
      >
        <app-icon :name="isNightMode ? 'light_mode' : 'dark_mode'" />
      </button>

      <div class="c-auth__panel">
        <img
          class="c-auth__mobile-logo"
          src="../../assets/betty-logo-2.svg"
          alt="Meridianbet"
        >

        <header class="c-auth__header">
          <span
            v-if="icon"
            class="c-auth__header-icon"
          >
            <app-icon
              :name="icon"
              :size="24"
            />
          </span>
          <h2 class="c-auth__title">
            {{ title }}
          </h2>
          <p
            v-if="subtitle"
            class="c-auth__subtitle"
          >
            {{ subtitle }}
          </p>
        </header>

        <slot />
      </div>
    </main>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AppIcon from '../app-icon/AppIcon';

export default {
  name: 'AuthLayout',
  components: {
    AppIcon,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    year: new Date().getFullYear(),
    features: [
      { icon: 'language', label: 'Websites & domains' },
      { icon: 'translate', label: 'Translations & languages' },
      { icon: 'tune', label: 'Configurations & settings' },
    ],
  }),
  computed: {
    ...mapGetters('theme', ['isNightMode']),
  },
  methods: {
    ...mapActions('theme', ['handleThemeToggle']),
  },
};
</script>

<style lang="scss" scoped>
.c-auth {
  display: flex;
  min-height: 100vh;
  font-family: var(--font-sans);
  background-color: var(--bg-app);
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
}

// ---------- brand panel ----------
.c-auth__brand {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 44%;
  max-width: 640px;
  padding: 48px 56px;
  overflow: hidden;
  color: #fff;
  background: linear-gradient(155deg, #c43d2f 0%, #9c2e23 55%, #5c1d17 100%);

  // fine dot grid for texture
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px);
    background-size: 22px 22px;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.9), transparent 75%);
    pointer-events: none;
  }
}

.c-auth__brand-glow {
  position: absolute;
  right: -180px;
  bottom: -180px;
  width: 520px;
  height: 520px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 179, 102, 0.35), transparent 65%);
  pointer-events: none;
}

.c-auth__logo {
  position: relative;
  width: 190px;
}

.c-auth__pitch {
  position: relative;
  max-width: 420px;
}

.c-auth__pitch-title {
  margin: 0 0 12px;
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.c-auth__pitch-text {
  margin: 0 0 32px;
  font-size: 16px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
}

.c-auth__features {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0;
}

.c-auth__feature {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 500;
}

.c-auth__feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  background-color: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(4px);
}

.c-auth__copyright {
  position: relative;
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

// ---------- form side ----------
.c-auth__main {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
}

.c-auth__theme-toggle {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  padding: 0;
}

.c-auth__panel {
  width: 100%;
  max-width: 400px;
  animation: c-auth-enter 0.45s var(--ease-out) both;
}

.c-auth__mobile-logo {
  display: none;
  width: 48px;
  margin-bottom: 24px;
}

.c-auth__header {
  margin-bottom: 32px;
}

.c-auth__header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 20px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-default);
  background-color: var(--bg-surface);
  color: var(--text-secondary);
  box-shadow: var(--shadow-xs);
}

.c-auth__title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--text-primary);
}

.c-auth__subtitle {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: var(--text-secondary);
}

@keyframes c-auth-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .c-auth__panel {
    animation: none;
  }
}

@media (max-width: 960px) {
  .c-auth__brand {
    display: none;
  }

  .c-auth__mobile-logo {
    display: block;
  }
}
</style>
